import {useCallback, useEffect, useState} from 'react';

import {Button, CheckBox, Modal, Select, SpinningLoader} from '@/components';
import type {AfriexCountry, CountryPermissions} from '@/types';
import {CountryStatus} from '@/types';
import {logger} from '@/utils/logger';

import {
  COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE,
  COUNTRY_AVAILABILITY_PERMISSIONS,
  resolvePermission,
} from './countryAvailability';

type AvailabilitySettingsModalProps = {
  onClose: VoidFunction;
  showModal: boolean;
  handleSave: (id: string, body: Partial<AfriexCountry>) => Promise<void>;
  item: AfriexCountry;
};

const STATUS_OPTIONS = [
  {key: 'Active', value: CountryStatus.ACTIVE},
  {key: 'Inactive (hidden from the app)', value: CountryStatus.INACTIVE},
];

const AvailabilitySettingsModal = ({
  onClose,
  showModal,
  handleSave,
  item,
}: AvailabilitySettingsModalProps) => {
  const [status, setStatus] = useState<CountryStatus>(CountryStatus.ACTIVE);
  const [permissions, setPermissions] = useState<CountryPermissions>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setStatus(item?.status ?? CountryStatus.ACTIVE);
  }, [item?.status]);

  useEffect(() => {
    setPermissions(item?.permissions ?? {});
  }, [item?.permissions]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setIsLoading(true);
      try {
        // `permissions` is seeded from the whole stored subtree and only the
        // two switches below are overwritten, so the six fields this screen
        // does not offer survive the save. They would not otherwise: the
        // update is a plain object, which Mongo turns into
        // `$set: {permissions}`, replacing the subtree entire. Wiping
        // `canScheduleTransaction` here would silently turn off recurring
        // send for the country.
        await handleSave(String(item?.id), {status, permissions});
        onClose();
      } catch (error) {
        // `handleSave` reports its own failures with a toast, so this only
        // fires if that reporting itself threw. The modal stays open either
        // way, so the edit is not silently discarded.
        logger.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [handleSave, item, onClose, permissions, status],
  );

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-11/12 md:w-6/12 lg:w-5/12 xl:w-4/12 px-4 sm:px-0"
      title={`${item?.name ?? ''} Availability`}
      closeOnOutsideClick
    >
      <div className="mx-5">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col mt-5">
            <div className="mb-5">
              <Select
                className="w-full"
                options={STATUS_OPTIONS}
                value={status}
                label="Status"
                placeholder="Status"
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(event.target.value as CountryStatus)
                }
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Inactive drops the country from the country list the app reads,
                so it stops appearing in the currency pickers. The app caches
                that list offline for a day, so allow up to 24 hours for the
                change to reach everyone.
              </p>
            </div>

            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
              {COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE}
            </p>

            {COUNTRY_AVAILABILITY_PERMISSIONS.map(
              ({key, label, hint, defaultWhenUnset}) => {
                const stored = permissions?.[key];
                const isUnset = stored === undefined || stored === null;
                return (
                  <div key={key}>
                    <CheckBox
                      id={key}
                      label={label}
                      checked={resolvePermission(
                        permissions,
                        key,
                        defaultWhenUnset,
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPermissions((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    <p className="ml-8 -mt-2 mb-3 text-xs text-slate-500 dark:text-slate-400">
                      {hint}
                      {isUnset
                        ? ` Currently unset, which the app reads as ${
                            defaultWhenUnset ? 'allowed' : 'not allowed'
                          }.`
                        : ''}
                    </p>
                  </div>
                );
              },
            )}

            <div className="flex w-full justify-end space-x-2 py-5">
              {/* Explicitly type="button": inside a <form> the default is
                  submit, so Cancel would have saved the very edit it is meant
                  to abandon. */}
              <Button type="button" onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button disabled={isLoading} type="submit" variant="solid">
                Submit
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default AvailabilitySettingsModal;
